import * as React from "react";
import { FC } from "react";
import { PopupBody, PopupFooter, PopupHeader, Popup } from "./Popup";
import { PopupRow } from "./PopupRow";
import { config, IPreview } from "../Map/config";
import { useNomaContext } from "../Contexts/MapContext";
import { CloseButton } from "../Elements/CloseButton";
import { DocsSection } from "./DocsSection";
import { PopupButton } from "../Elements/PopupButton";
import { Feature } from "ol";
import { useCustomViewContext } from "../Contexts/CustomViewContext";
import { IRelatedDocs } from "../interfaces";
import "./Preview.scss";
import "./Print.scss";

export const Preview: FC = () => {
    const {
        isMobile,
        activeTab,
        previewFeature,
        setPreviewFeature,
        setApplyFeature,
        setRegisterFeature,
        setSideViewVisible,
        setLayersMenuVisible
    } = useNomaContext();

    const { relatedDocs } = useCustomViewContext();

    const x = 80; // popup to the left

    const previewConfig = config.dataView.tabs.find((tabConfig) => tabConfig.tabValue === activeTab).preview;

    const isAuction =
        previewFeature.get("TYPEOFLEASEGRANTING") !== 1 && previewFeature.get("TYPEOFLEASEGRANTING") !== 5;

    const objectId = previewFeature.get("OBJECTID");
    const objectDocs = relatedDocs.find((item: IRelatedDocs) => (item.objectId === objectId ? item.docs : undefined));

    const onClickApply = () => {
        isAuction ? setApplyFeature({ feature: previewFeature, isRight: false }) : setRegisterFeature(previewFeature);
        setSideViewVisible(false);
        setLayersMenuVisible(false);
    };

    const onClickZip = () =>
        (window.location.href = config.dataView.docsApi + "/files/related/PUBSDE.LandPlotCatalog/" + objectId);

    const deadlineNotReached = isBeforeDeadline(previewFeature);
    return (
        <Popup id={"preview-popup"} isMobile={isMobile} hasBackdrop={false} defaultPosition={{ x, y: 0 }}>
            <PopupHeader isMobile={isMobile}>
                <CloseButton onClose={() => setPreviewFeature(null)}></CloseButton>
            </PopupHeader>
            <PopupBody isMobile={isMobile}>
                <PreviewContent previewConfig={previewConfig}></PreviewContent>
            </PopupBody>
            <PopupFooter isMobile={isMobile} hasSingleButton={!deadlineNotReached}>
                {activeTab === 1 && (
                    <>
                        {deadlineNotReached && (
                            <PopupButton title={"Pieteikties"} icon={"icon-zemesnoma-forward"} onClick={onClickApply} />
                        )}
                        <PopupButton
                            title={"Lejupielādēt kā ZIP"}
                            icon={"icon-zemesnoma-forward"}
                            onClick={onClickZip}
                            disabled={!objectDocs}
                        ></PopupButton>
                    </>
                )}
            </PopupFooter>
        </Popup>
    );
};

const PreviewContent = ({ previewConfig }: { previewConfig: IPreview }) => {
    const { previewFeature, setLocateFeature, getFieldAlias, getFieldValue, snapshotBig, snapshotSmall } =
        useNomaContext();

    const print = () => {
        const originalPopup = document.getElementById("preview-popup");
        const content = originalPopup.getElementsByClassName("popup-body")[0].cloneNode(true);
        content.firstChild.parentElement.id = "print-popup";

        const iframe = document.createElement("iframe");
        iframe.id = "printPreview";
        iframe.name = "printPreview";
        iframe.srcdoc = '<html><head><link rel="stylesheet" type="text/css" href="./zemesnoma-map.css"></head><body>';

        iframe.onload = () => {
            iframe.contentWindow.document.body.appendChild(content);
            iframe.contentWindow.onafterprint = () => {
                document.body.removeChild(iframe);
            };
            window.frames["printPreview"].focus();
            window.frames["printPreview"].print();
        };
        document.body.appendChild(iframe);
    };

    return (
        <>
            <div className="description-title">Nomas objekts</div>
            <div className="preview-header">
                {previewFeature.get("LAYERID") === "1" ? (
                    <WindFarmHeader
                        feature={previewFeature}
                        getFieldAlias={getFieldAlias}
                        getFieldValue={getFieldValue}
                    />
                ) : (
                    <LandPlotHeader
                        feature={previewFeature}
                        getFieldAlias={getFieldAlias}
                        getFieldValue={getFieldValue}
                    />
                )}
                <div className="header-price">
                    <div className="price-background">
                        <div className="price-amount">{getFieldValue(previewFeature, "STARTRENT")}</div>
                        <div className="price-text">gadā</div>
                        <div className="price-text">(bez NĪN un PVN)</div>
                    </div>
                </div>
                <div className="snapshots-block">
                    <img className="snapshot" src={snapshotBig}></img>
                    <img
                        className="snapshot detailed-snapshot"
                        src={snapshotSmall}
                        onClick={() => setLocateFeature(previewFeature)}
                    ></img>
                </div>
            </div>
            <hr></hr>
            <div className="print-button-container">
                <a role="button" className="print-button" onClick={print}>
                    <span className="icon-zemesnoma-printer"></span>
                </a>
            </div>
            {previewConfig.fields.map((fieldName, i) => (
                <PreviewRow
                    fieldName={fieldName}
                    previewConfig={previewConfig}
                    key={"preview-row-" + fieldName + i}
                ></PreviewRow>
            ))}
            <hr></hr>
            <div className="preview-footer">{previewConfig.footerText}</div>
            <DocsSection feature={previewFeature} title={"Pievienotie dokumenti: "}></DocsSection>
        </>
    );
};

const PreviewRow = ({ fieldName, previewConfig }: { fieldName: string; previewConfig: IPreview }) => {
    const { previewFeature, getFieldValue } = useNomaContext();

    if (fieldName.startsWith("hardcoded-")) {
        const fieldConfig = previewConfig.hardcodedFields.find((hcField) => hcField.field === fieldName);

        return (
            <div className="preview-row" key={"preview-row-" + fieldName}>
                <div className="preview-row-title">{fieldConfig.alias + ": "}</div>
                <div className="preview-values-column">
                    {fieldConfig.values.map((value) => {
                        const fieldvalue = getFieldValue(previewFeature, value);
                        return <div key={"hc-value-" + value}>{fieldvalue || value} </div>;
                    })}
                </div>
            </div>
        );
    }

    return (
        <PopupRow
            fieldName={fieldName}
            feature={previewFeature}
            classname={"preview-row"}
            key={"preview-row" + fieldName}
        ></PopupRow>
    );
};

const LandPlotHeader = ({ feature, getFieldAlias, getFieldValue }) => {
    return (
        <div className="header-description">
            <div className="description-line">
                {getFieldValue(feature, "REGIONATVK", "0") ? (
                    <span>{getFieldValue(feature, "REGIONATVK", "0") + ", "}</span>
                ) : null}
                {getFieldValue(feature, "MUNICIPALITYATVK", "0") ? (
                    <span>{getFieldValue(feature, "MUNICIPALITYATVK", "0") + ", "}</span>
                ) : null}
                {getFieldValue(feature, "PROPERTYTITLE", "0") ? (
                    <span>{getFieldValue(feature, "PROPERTYTITLE", "0")}</span>
                ) : null}
            </div>
            <div className="description-line">
                <span>{getFieldAlias("PROPERTYCADASTRALNUMBER", "0") + ": "}</span>
                <span>{getFieldValue(feature, "PROPERTYCADASTRALNUMBER", "0")}</span>
            </div>
            <div className="description-line">
                <span>{getFieldAlias("CADASTER", "0") + ": "}</span>
                <span>{getFieldValue(feature, "CADASTER", "0")}</span>
            </div>
        </div>
    );
};
const WindFarmHeader = ({ feature, getFieldAlias, getFieldValue }) => (
    <div className="header-description">
        <div className="description-line">{getFieldValue(feature, "PROPERTYTITLE", "1") || null}</div>
        <div className="description-line">
            <span>{getFieldAlias("MW", "1") + ": "}</span>
            <span>{getFieldValue(feature, "MW", "1")}</span>
        </div>
    </div>
);

export const isBeforeDeadline = (feature: Feature): boolean => {
    const end = feature.get("APPLICATIONENDDATE");
    if (!end) {
        return true;
    }
    const endDate = new Date(end);
    const endDateLastHour = endDate && endDate.setHours(24, 0, 0, 0);
    return endDateLastHour && Date.now() < endDateLastHour;
};
